import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Star, Phone, WhatsappLogo, CheckCircle, User } from "@phosphor-icons/react";
import { submitLead, getFilterOptions } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../lib/AuthProvider";

const COURSE_OPTIONS = ["MBBS", "BDS", "BAMS", "BHMS", "BUMS", "B.Sc Nursing", "BPT", "Other"];
const COLLEGE_TYPES = ["Government", "Private", "Both"];

// Header + button theming keyed by callback type (mirrors each plan page's palette).
const THEME = {
  General:   { grad: "from-amber-400 to-orange-500", btn: "from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700" },
  Priority:  { grad: "from-violet-500 to-indigo-600", btn: "from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700" },
  NRI:       { grad: "from-amber-400 to-yellow-500", btn: "from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700" },
  Affiliate: { grad: "from-pink-500 to-rose-500", btn: "from-pink-500 to-rose-600 hover:from-pink-600 hover:to-rose-700" },
};

/**
 * Lead-capture modal used by the "Enquire Fees" CTA on the Fresh Colleges rail
 * (any card whose college has no `annual_fees` on record).
 *
 * On submit → POST /api/leads → stored in `leads` collection. When auth ships
 * later we can prefill the form from a logged-in user profile.
 *
 * Design mirrors the "Get Priority Callback" reference:
 *   – warm amber header strip
 *   – 4 inputs (name*, email, phone*, NEET rank) + optional message
 *   – dual CTA: "Request Callback" (primary) + "Chat on WhatsApp" (secondary)
 */
export const LeadForm = ({ open, onOpenChange, college, plan = null, callbackType = "General", defaultMessage = "" }) => {
  const { user } = useAuth();
  const affiliate = callbackType === "Affiliate";
  const counsellingPreset = callbackType === "Priority" || callbackType === "NRI";
  const theme = THEME[callbackType] || THEME.General;
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [stateSel, setStateSel] = useState("");
  const [rank, setRank] = useState("");
  const [course, setCourse] = useState("");
  const [counselling, setCounselling] = useState(counsellingPreset ? "yes" : "");
  const [collegeTypes, setCollegeTypes] = useState([]);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [states, setStates] = useState([]);

  useEffect(() => {
    if (!open || affiliate || states.length) return;
    getFilterOptions()
      .then((res) => setStates(res.data.states || []))
      .catch(() => setStates([]));
  }, [open, affiliate, states.length]);

  // Prefill the message when a default is supplied (e.g. affiliate enquiry).
  useEffect(() => {
    if (open && defaultMessage) setMsg(defaultMessage);
  }, [open, defaultMessage]);

  const toggleType = (t) =>
    setCollegeTypes((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));

  // When a Google-signed-in user opens the form, prefill name/email from
  // their verified profile. We don't ASK them again for these — the backend
  // also stamps user_uid + user_email onto the lead from the ID token.
  useEffect(() => {
    if (open && user) {
      setName(user.displayName || "");
      setEmail(user.email || "");
    }
  }, [open, user]);

  const reset = () => { setName(""); setEmail(""); setPhone(""); setStateSel(""); setRank(""); setCourse(""); setCounselling(counsellingPreset ? "yes" : ""); setCollegeTypes([]); setMsg(""); setDone(false); };
  const close = () => { reset(); onOpenChange(false); };

  const submit = async (e) => {
    e.preventDefault();
    if (affiliate) {
      if (!phone.trim()) { toast.error("Mobile number is required"); return; }
    } else if (!phone.trim() || (!user && !name.trim()) || !stateSel) {
      toast.error(!stateSel ? "Please select your state" : (user ? "Phone number is required" : "Name and phone are required"));
      return;
    }
    setBusy(true);
    try {
      await submitLead(affiliate ? {
        name: (name || user?.displayName || "Affiliate Partner").trim(),
        email: (email || user?.email || "").trim() || undefined,
        phone: phone.trim(),
        message: msg.trim() || undefined,
        callback_type: "Affiliate",
        college_name: "Affiliate program enquiry",
        lead_source: "affiliate_enquiry",
      } : {
        name: (name || user?.displayName || "").trim(),
        email: (email || user?.email || "").trim() || undefined,
        phone: phone.trim(),
        state: stateSel || undefined,
        neet_rank: rank ? parseInt(rank, 10) : undefined,
        course_of_interest: course || undefined,
        personalized_counselling: counselling === "" ? undefined : counselling === "yes",
        college_type_interest: collegeTypes.length ? collegeTypes : undefined,
        callback_type: callbackType,
        message: msg.trim() || undefined,
        college_code: college?.college_code,
        college_name: college?.primary_name || (plan ? `Plan enquiry: ${plan}` : undefined),
        lead_source: plan ? `plan_enquiry` : "fees_enquiry",
      });
      setDone(true);
      toast.success("We'll call you back within 24 hours");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const waLink = () => {
    const text = encodeURIComponent(
      plan
        ? `Hi Unicators, I'm interested in the "${plan}" plan. Please share the details and next steps.`
        : `Hi, I want to know fees & seat details for ${college?.primary_name || "this college"} (${college?.college_code || ""}). My NEET rank is ${rank || "____"}.`,
    );
    return `https://wa.me/919986143554?text=${text}`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="p-0 max-w-[540px] gap-0 border-2 border-zinc-200 max-h-[90vh] overflow-y-auto" data-testid="lead-form-dialog">
        {/* Themed header */}
        <div className={`bg-gradient-to-br ${theme.grad} p-5 text-white`}>
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 bg-white/25 backdrop-blur-sm flex items-center justify-center rounded-lg shrink-0">
              <Star size={22} weight="fill" className="text-white" />
            </div>
            <div className="min-w-0">
              <DialogHeader>
                <DialogTitle className="text-lg font-bold text-white leading-tight">
                  {plan ? "Talk to a Counsellor" : "Get Priority Callback"}
                </DialogTitle>
                <DialogDescription className="text-white/85 text-xs mt-1">
                  {done ? "Thanks — we're on it" : (plan ? `${plan} — request a free consultation` : `Fees & counselling details for ${college?.primary_name || "this college"}`)}
                </DialogDescription>
              </DialogHeader>
            </div>
          </div>
        </div>

        {done ? (
          <div className="p-8 text-center space-y-3" data-testid="lead-form-success">
            <CheckCircle size={44} weight="fill" className="text-emerald-500 mx-auto" />
            <div className="text-lg font-bold text-[#09090B]">Request received!</div>
            <p className="text-sm text-zinc-600">Our team will call you at <span className="font-mono font-bold text-[#09090B]">{phone}</span> within 24 hours{affiliate ? " to discuss the partnership." : " with fees, seat matrix, and cutoff data."}</p>
            <button onClick={close} className="text-xs font-bold uppercase tracking-wider text-[#6043EF] hover:underline mt-2" data-testid="lead-form-close">
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-5 space-y-3">
            {!affiliate && (user ? (
              /* Signed-in state — show identity chip instead of name/email inputs */
              <div className="flex items-center gap-2 p-2 rounded-lg bg-emerald-50 border border-emerald-200" data-testid="lead-form-signed-in">
                {user.photoURL ? (
                  <img src={user.photoURL} alt="" className="w-8 h-8 rounded-full object-cover" referrerPolicy="no-referrer" />
                ) : (
                  <span className="w-8 h-8 rounded-full bg-emerald-500 text-white flex items-center justify-center">
                    <User size={14} weight="fill" />
                  </span>
                )}
                <div className="min-w-0 flex-1">
                  <div className="text-[12px] font-bold text-[#09090B] truncate">{user.displayName || "—"}</div>
                  <div className="text-[10px] text-zinc-500 truncate">{user.email}</div>
                </div>
                <span className="text-[9px] font-bold uppercase tracking-wider text-emerald-700">Signed In</span>
              </div>
            ) : (
              <>
                <div>
                  <Label htmlFor="lead-name" className="text-[11px] font-bold uppercase tracking-wider text-zinc-500">Your Name *</Label>
                  <Input id="lead-name" value={name} onChange={(e) => setName(e.target.value)}
                    placeholder="Full name" data-testid="lead-form-name" required className="mt-1" />
                </div>
                <div>
                  <Label htmlFor="lead-email" className="text-[11px] font-bold uppercase tracking-wider text-zinc-500">Email</Label>
                  <Input id="lead-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com" data-testid="lead-form-email" className="mt-1" />
                </div>
              </>
            ))}
            <div className={affiliate ? "" : "grid grid-cols-2 gap-3"}>
              <div>
                <Label htmlFor="lead-phone" className="text-[11px] font-bold uppercase tracking-wider text-zinc-500">Mobile Number *</Label>
                <Input id="lead-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                  placeholder="10-digit mobile" data-testid="lead-form-phone" required className="mt-1" />
              </div>
              {!affiliate && (
                <div>
                  <Label htmlFor="lead-state" className="text-[11px] font-bold uppercase tracking-wider text-zinc-500">State/Domicile *</Label>
                  <select id="lead-state" value={stateSel} onChange={(e) => setStateSel(e.target.value)}
                    data-testid="lead-form-state" required
                    className="mt-1 w-full h-10 rounded-md border border-zinc-200 bg-white px-3 text-sm text-[#09090B] focus:outline-none focus:ring-2 focus:ring-[#6043EF]/40">
                    <option value="">Select Your State</option>
                    {states.map((s) => (<option key={s} value={s}>{s}</option>))}
                  </select>
                </div>
              )}
            </div>

            {!affiliate && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="lead-rank" className="text-[11px] font-bold uppercase tracking-wider text-zinc-500">2026 NEET Rank</Label>
                <Input id="lead-rank" type="number" min="1" value={rank} onChange={(e) => setRank(e.target.value)}
                  placeholder="e.g. 15000" data-testid="lead-form-rank" className="mt-1" />
              </div>
              <div>
                <Label htmlFor="lead-course" className="text-[11px] font-bold uppercase tracking-wider text-zinc-500">Course of Interest</Label>
                <select id="lead-course" value={course} onChange={(e) => setCourse(e.target.value)}
                  data-testid="lead-form-course"
                  className="mt-1 w-full h-10 rounded-md border border-zinc-200 bg-white px-3 text-sm text-[#09090B] focus:outline-none focus:ring-2 focus:ring-[#6043EF]/40">
                  <option value="">Select Course</option>
                  {COURSE_OPTIONS.map((c) => (<option key={c} value={c}>{c}</option>))}
                </select>
              </div>
            </div>
            )}

            {!affiliate && !counsellingPreset && (
            <div>
              <Label className="text-[11px] font-bold uppercase tracking-wider text-zinc-500">Personalized Counselling?</Label>
              <div className="mt-1 flex items-center gap-4 h-10">
                {["yes", "no"].map((v) => (
                  <button key={v} type="button" onClick={() => setCounselling(v)}
                    data-testid={`lead-form-counselling-${v}`}
                    className={`flex items-center gap-1.5 text-sm font-semibold transition-colors ${counselling === v ? "text-[#6043EF]" : "text-zinc-500 hover:text-zinc-700"}`}>
                    <span className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${counselling === v ? "border-[#6043EF]" : "border-zinc-300"}`}>
                      {counselling === v && <span className="w-2 h-2 rounded-full bg-[#6043EF]" />}
                    </span>
                    {v === "yes" ? "Yes" : "No"}
                  </button>
                ))}
              </div>
            </div>
            )}

            {!affiliate && (
            <div>
              <Label className="text-[11px] font-bold uppercase tracking-wider text-zinc-500">Interested in Which Type of College?</Label>
              <div className="mt-1.5 flex flex-wrap gap-2" data-testid="lead-form-college-types">
                {COLLEGE_TYPES.map((t) => {
                  const on = collegeTypes.includes(t);
                  return (
                    <button key={t} type="button" onClick={() => toggleType(t)}
                      data-testid={`lead-form-type-${t.toLowerCase()}`}
                      className={`px-3 py-1.5 rounded-full text-xs font-bold border-2 transition-all ${on ? "border-[#6043EF] bg-[#6043EF]/10 text-[#6043EF]" : "border-zinc-200 text-zinc-500 hover:border-zinc-300"}`}>
                      {t}
                    </button>
                  );
                })}
              </div>
            </div>
            )}

            <div>
              <Label htmlFor="lead-msg" className="text-[11px] font-bold uppercase tracking-wider text-zinc-500">Message (optional)</Label>
              <Textarea id="lead-msg" value={msg} onChange={(e) => setMsg(e.target.value)}
                placeholder="Anything specific you want to know?" data-testid="lead-form-message"
                rows={2} className="mt-1 resize-none" />
            </div>

            <button type="submit" disabled={busy}
              data-testid="lead-form-submit"
              className={`w-full h-11 bg-gradient-to-r ${theme.btn} text-white font-bold text-sm uppercase tracking-wider flex items-center justify-center gap-2 disabled:opacity-60 transition-all shadow-[3px_3px_0px_0px_#09090B]`}>
              <Phone size={16} weight="fill" />
              {busy ? "Submitting…" : (affiliate ? "Join as Affiliate" : "Request Callback")}
            </button>

            <a href={waLink()} target="_blank" rel="noopener noreferrer"
              data-testid="lead-form-whatsapp"
              className="w-full h-11 bg-[#25D366] hover:bg-[#20b957] text-white font-bold text-sm uppercase tracking-wider flex items-center justify-center gap-2 transition-colors">
              <WhatsappLogo size={18} weight="fill" />
              Chat on WhatsApp
            </a>

            <p className="text-[10px] text-center text-zinc-400 pt-1">
              <Star size={9} weight="fill" className="inline text-zinc-400 -mt-0.5" /> Featured admissions partner on Unicators
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default LeadForm;
